/**
 * Utility for mergeNodes to merge to fields.
 * It prioritize the value from A over B when conflicting.
 */
export function mergeLeafsProperty<T extends object, K extends keyof T>(
    property: K,
    ancestor: T | null | undefined,
    base: T,
    head: T,
    options: {
        /**
         * Set to true if this property can be removed.
         * If a removable property is missing from ancestor and head, it'll be taken from base.
         */
        isRemovable: boolean;
    }
): T[K] {
    // Without an ancestor, we can't determine if a change is a conflict or not, so we take the value from head.
    if (!ancestor) {
        return head[property];
    }

    // If a property is removable, we need to check if it was added or removed in head compared to ancestor.
    if (options.isRemovable) {
        const ancestorHas = Object.hasOwn(ancestor, property);
        const headHas = Object.hasOwn(head, property);

        const neitherHave = !ancestorHas && !headHas;
        const bothHaveSameValue =
            ancestorHas && headHas && Object.is(ancestor[property], head[property]);

        // If neither the ancestor or head have the property, or if they both have the same value, we can take the value from base
        if (neitherHave || bothHaveSameValue) {
            return base[property];
        } else {
            // otherwise the property has changed (either added, removed or changed value) in head compared to ancestor, we take the value from head
            return head[property];
        }
    }

    // If a property is present in the ancestor and has the same value in head, it means it was not changed in head, so we can take the value from base (which could be different from ancestor if it was changed in base).
    if (Object.is(ancestor[property], head[property])) {
        return base[property];
    }

    return head[property];
}
